<template>
  <section>
    <v-row>
      <v-col>
        <h2 class="secondary--text mr-8">My appointments</h2>
      </v-col>
      <v-spacer></v-spacer>
      <div class="ml-auto mt-3">
        <primary-button
          class="mr-3"
          :loading="buildingExcelData"
          before-icon="microsoft-excel"
          small
          @click="exportToExcel(exportableRows, excelFileName)">
          Export to Excel
        </primary-button>
      </div>
    </v-row>
    <v-card class="appointment-list mt-3">
      <v-card-title>
        <v-row align="center">
          <v-col md="5" sm="12" class="pa-0">
            <text-field
              class="appointment-list-field"
              height="35"
              v-model="filters.searchStr"
              append-icon="mdi-magnify"
              label="Search appointment"
              @input="filters.dateRange = []"
              single-line
              hide-details>
            </text-field>
          </v-col>
          <v-col md="3" sm="12" class="pa-0">
            <date-picker
              @input="updateSelectedDateButtonFilter"
              v-model="filters.dateRange"
              format="MMM D, YYYY"
              height="35"
              inner-icon
              no-input-mode
              is-range
              single-line
              hide-details>
            </date-picker>
          </v-col>
          <v-col md="4" sm="12" class="d-flex justify-end pa-0">
            <v-btn-toggle @change="changeDateFilter" v-model="selectedDateButtonFilter" mandatory>
              <outline-button color="secondary" small value="today"> TODAY </outline-button>
              <outline-button color="secondary" small value="week"> NEXT 7 DAYS </outline-button>
              <outline-button color="secondary" small :value="null">
                ALL APPOINTMENTS
              </outline-button>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-title>
      <div class="is-relative">
        <appointments-loader :is-loading="loading"></appointments-loader>
        <v-data-table
          :headers="headers"
          :items="listableAppointments"
          ref="appointmentListTable"
          :sort-desc.sync="sortDesc"
          :search="regexSafeSearch"
          :custom-filter="searchRows"
          @current-items="setExportRows"
          item-key="id"
          :expanded.sync="expanded"
          :footer-props="footerProps"
          @update:page="handlePagination"
          class="clickable-rows"
          :options.sync="options">
          <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ value }">
            <template v-if="header.value === 'user.email'">
              <copy-content
                :key="header.value"
                :content="value"
                label="Click to copy carrier email">
                <span :inner-html.prop="value | highlight(regexSafeSearch)"></span>
              </copy-content>
            </template>
            <template v-else>
              <span
                :key="header.value"
                :inner-html.prop="value | highlight(regexSafeSearch)"></span>
            </template>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <div :class="`${item.status.toLowerCase()} tile font-size-x-small text-center`">
              <v-icon class="mr-1" v-if="novaCore.isRequested(item.status)">mdi-clock-alert</v-icon>
              <strong
                :class="[
                  { 'black--text': novaCore.isRequested(item.status) },
                  item.status.toLowerCase()
                ]"
                :inner-html.prop="
                  novaCore.breakWordsAtCaps(item.status) | highlight(regexSafeSearch)
                "></strong>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="row-actions">
              <icon-tooltip-button
                icon-class="mr-2"
                size="large"
                @click="handleEventClick(item)"
                tooltip="View Appointment"
                icon="eye"></icon-tooltip-button>
              <icon-tooltip-button
                size="large"
                @click="toggleRowExpansion(item)"
                tooltip="Show More"
                :icon="
                  expanded.includes(item) ? 'chevron-up' : 'chevron-down'
                "></icon-tooltip-button>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers, item }" v-if="headers && headers.length">
            <td :colspan="headers.length" class="py-6">
              <h3>Tags</h3>
              <span
                v-for="tag in item.tags"
                :key="tag"
                :inner-html.prop="tag | highlight(regexSafeSearch, item.id, 'tag')"></span>
              <span v-if="!item.tags || !item.tags.length"
                >This appointment does not have tags</span
              >

              <v-divider class="my-5"></v-divider>

              <h3>Custom Fields</h3>
              <div v-for="customField in filteredCustomFields(item)" :key="customField.name">
                <strong>{{ customField.label }}</strong
                >:
                <span
                  :inner-html.prop="
                    getCustomFieldValue(customField, item)
                      | highlight(regexSafeSearch, item.id, 'customField')
                  "></span>
              </div>
            </td>
          </template>

          <template v-slot:[`item.start`]="{ item }">
            <span>{{
              getTimeInWarehouseTimezone(
                item,
                `${novaCore.LuxonDateTimeFormats.LongDateShortMonth} @ ${novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`,
                `${novaCore.LuxonDateTimeFormats.LongDateShortMonth} @ ${novaCore.LuxonDateTimeFormats.Extended24HrTime} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`
              )
            }}</span>
          </template>

          <template v-slot:no-data v-if="shouldShowNoSelectionOverlayMargin">
            <div style="height: 350px"></div>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </section>
</template>

<script>
import dataListMixin from '@satellite/components/mixins/dataListMixin';
import exportAppointmentsMixin from '@satellite/components/mixins/exportAppointmentsMixin';
import appointmentMixin from '@/components/mixins/appointmentMixin';

export default {
  mixins: [dataListMixin, appointmentMixin, exportAppointmentsMixin],
  props: {
    showExportButton: {
      type: Boolean,
      required: false,
      default: true
    },
    buildHeadersFn: {
      type: Function,
      required: false,
      default: null
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 15
    }
  },
  data() {
    return {
      appointments: [],
      sortBy: ['start'],
      filters: {
        searchStr: null,
        dateRange: this.isRange ? [] : ''
      },
      expanded: [],
      options: {
        itemsPerPage: this.itemsPerPage
      },
      loading: false,
      hasPageUpdated: false,
      selectedDateButtonFilter: null
    };
  },
  filters: {
    highlight(value, query, itemId, type) {
      if (typeof value !== 'string') {
        return value;
      }

      let r = value?.replace(
        new RegExp(query, 'ig'),
        v => `<span class="orange lighten-3">${v}</span>`
      );

      if (type === 'tag') {
        r = `${r}, `;
      }

      return r;
    }
  },
  computed: {
    rangeValues() {
      const baseMoment = () => momentjs(new Date()).clone();
      const inputFormat = 'YYYY-MM-DD';

      return {
        today: [baseMoment().format(inputFormat), baseMoment().format(inputFormat)],
        week: [
          baseMoment().add(1, 'day').format(inputFormat),
          baseMoment().add(8, 'day').format(inputFormat)
        ]
      };
    },
    regexSafeSearch() {
      if (this.filters.searchStr) {
        return _.escapeRegExp(this.filters.searchStr);
      }

      return this.filters.searchStr;
    },
    totalItems() {
      return this.$responseMetaData?.total;
    },
    excelFileName() {
      return `${this.$warehouse?.name || 'opendock'}_appointments.xlsx`;
    },
    listableAppointments() {
      return this.appointments.filter(appointment => !this.novaCore.isReserve(appointment));
    },
    shouldShowNoSelectionOverlayMargin() {
      return !this.$warehouse?.id || !this.$selectedDocks?.length;
    },
    headers() {
      if (this.buildHeadersFn) {
        return this.buildHeadersFn();
      }

      return [
        { text: 'Conf #', value: 'confirmationNumber', searchable: true },
        { text: 'Start Time', value: 'start', searchable: true },
        { text: 'Carrier Contact', value: 'user.email', searchable: true },
        { text: 'Carrier Company', value: 'user.company.name', searchable: true, sortable: true },
        {
          text: 'Location',
          value: 'dock.warehouse.name',
          searchable: true,
          align: ''
        },
        { text: 'Dock', value: 'dock.name', searchable: true },
        { text: 'Load Type', value: 'loadType.name', searchable: true },
        { text: 'Direction', value: 'loadType.direction', searchable: true },
        { text: 'Tags', value: 'tags', searchable: true, align: ' d-none' },
        { text: 'Custom Fields', value: 'customFields', searchable: true, align: ' d-none' },
        {
          text: this.$consolidatedSettings.referenceNumberDisplayName,
          value: 'refNumber',
          searchable: true
        },
        { text: 'Status', value: 'status', searchable: true },
        { text: 'Appointment ID', value: 'id', searchable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
      ];
    },
    warehouse() {
      // This is used by appointmentMixin
      return this.$warehouse;
    }
  },
  methods: {
    searchRows(value, search, item) {
      if (!value) {
        return false;
      }
      if (!search) {
        return true;
      }
      let hasMatch = false;
      let hasExpandedMatch = false;
      const isArr = Array.isArray(value);

      const s = search.toLowerCase();

      if (typeof value === 'string') {
        hasMatch = value.toLowerCase().search(s) !== -1;
      }

      if (isArr && !hasMatch) {
        const filtered = value.filter(i => {
          i = i && typeof i === 'object' ? Object.values(i).join(', ') : i;
          return typeof i === 'string' ? i.toLowerCase().search(s) !== -1 : false;
        });
        hasMatch = filtered.length > 0;
        hasExpandedMatch = hasMatch;
      }

      if (typeof value === 'object' && !isArr && !hasMatch) {
        hasMatch =
          Object.values(value).filter(i => {
            return i.toLowerCase().search(s) !== -1;
          }).length > 0;
        hasExpandedMatch = hasMatch;
      }

      if (hasExpandedMatch) {
        this.expanded.push(item);
      }

      return hasMatch;
    },
    getCustomFieldValue(customField, appointment) {
      if (!customField.value) {
        return '----';
      }

      return this.novaCore.getCustomFieldFormattedValue(customField, {
        [this.novaCore.CustomFieldType.Timestamp]: {
          timezone: appointment.dock.warehouse.timezone,
          formatAsMilitary: this.$isMilitaryTimeEnabled(this.$org)
        }
      });
    },
    toggleRowExpansion(item) {
      if (this.expanded.includes(item)) {
        const index = this.expanded.findIndex(i => i === item);
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(item);
      }
    },
    // TODO this function is used in several places, it needs to be made a UTIL
    getTimeInWarehouseTimezone(appointment, format12, format24) {
      return this.novaCore.transformForUserFriendlyTimezone(
        this.novaCore.formatDateTimeWithMilitarySupport(
          appointment.utcStart ?? appointment.start,
          appointment.dock.warehouse.timezone,
          format12,
          this.$isMilitaryTimeEnabled(this.$warehouse),
          format24
        ),
        appointment.dock.warehouse.timezone
      );
    },
    updateSelectedDateButtonFilter() {
      this.selectedDateButtonFilter = null;

      Object.keys(this.rangeValues).forEach(range => {
        if (_.isEqual(this.filters.dateRange, this.rangeValues[range])) {
          this.selectedDateButtonFilter = range;
        }
      });
    },
    changeDateFilter(value) {
      // We can't use the v-model, it would make the datepicker choices
      // be cancelled due the two-way binding.
      this.filters.dateRange = value ? this.rangeValues[value] : [];
    },
    async getData() {
      this.expanded = [];
      if (this.shouldGetData(this.filters) && !this.hasPageUpdated) {
        this.loading = true;
        let query = this.getQueryBase();
        query.limit = -1;
        if (this.filters.dateRange.length === 2) {
          query.s = {
            start: {
              $between: [
                momentjs(this.filters.dateRange[0]).toDate(),
                momentjs(this.filters.dateRange[1]).endOf('day').toDate()
              ]
            }
          };
        }
        try {
          this.appointments = await this.$store.dispatch('Appointments/getAppointments', query);
        } finally {
          this.loading = false;
        }
      }
      this.hasPageUpdated = false;
    },
    setExportRows() {
      if (this.$refs.appointmentListTable) {
        this.exportableRows = this.novaCore.deepClone(
          this.$refs.appointmentListTable?.$children[0].filteredItems
        );
      }
    },
    handleEventClick(event) {
      this.$router.push({ path: `/warehouse/${this.$warehouseId}/appointments/${event.id}` });
    },
    shouldGetData(filters) {
      return filters.dateRange.length !== 1;
    },
    filteredCustomFields(event) {
      if (event) {
        return this.novaCore
          .updateCustomFieldsFromWarehouse(event.customFields, event.dock.warehouse, true)
          .filter(field => !field.hiddenFromCarrier);
      }
      return [];
    },
    handlePagination() {
      this.hasPageUpdated = true;
    }
  },
  async mounted() {
    // Default range is now today
    this.filters.dateRange = this.rangeValues.today;
  },
  watch: {
    'filters.searchStr'() {
      if (!this.filters.searchStr) {
        this.expanded = [];
      }
    },
    'filters.dateRange'() {
      this.$nextTick(() => {
        if (this.filters.dateRange.length === 1) {
          return;
        }
        this.options.page = 1;
      });
    }
  }
};
</script>
